import React from 'react';
import { FaTimes } from 'react-icons/fa';
import { formatPhoneForDisplay } from '../../utils/phone';
import { formatDisplayDate, formatDisplayDateTime, formatDisplayTime } from '../../utils/dateFormat';
import './AccountLayout.css';

function formatValue(value) {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

function formatSubmittedAt(value) {
  return formatDisplayDateTime(value, '—');
}

function formatEventDate(value) {
  return formatDisplayDate(value, '—');
}

function formatEventTime(value) {
  return formatDisplayTime(value, '—');
}

function DetailGrid({ title, rows }) {
  return (
    <section>
      <h3>{title}</h3>
      <dl className="account-sub-detail-grid">
        {rows.map(([label, value]) => (
          <div key={label} className={label === 'Venue' || label === 'Menu' ? 'account-sub-detail-grid__span' : undefined}>
            <dt>{label}</dt>
            <dd>{formatValue(value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function menuSummary(payload = {}) {
  const menu = payload.menuDetails || {};
  if (menu.type === 'custom') {
    return 'Custom Menu';
  }
  if (menu.type === 'standard') {
    const addons = Array.isArray(menu.addons) && menu.addons.length
      ? ` · Add-ons: ${menu.addons.join(', ')}`
      : '';
    return `${menu.packageName || 'Package'}${addons}`;
  }
  return '—';
}

function menuItemLines(payload = {}) {
  const menu = payload.menuDetails || {};
  const toLine = (item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') {
      if (item.name && item.category) return `[${item.category}] ${item.name}`;
      return item.name || item.label || '';
    }
    return String(item || '');
  };
  if (menu.type === 'custom') {
    return (Array.isArray(menu.items) ? menu.items : []).map(toLine).filter(Boolean);
  }
  if (menu.type === 'standard') {
    const items = (Array.isArray(menu.packageItems) ? menu.packageItems : []).map(toLine).filter(Boolean);
    const addons = (Array.isArray(menu.addons) ? menu.addons : []).map((a) => `Add-on: ${toLine(a)}`);
    return [...items, ...addons];
  }
  return [];
}

/**
 * @param {{ enquiry: object, onClose: () => void }} props
 */
export default function AccountBulkOrderDetailsModal({ enquiry, onClose }) {
  if (!enquiry) return null;

  const payload = enquiry.payload || {};
  const eventType = payload.eventType === 'Other'
    ? `Other — ${payload.otherEventType || ''}`.trim()
    : payload.eventType;
  const referenceId = payload.referenceId || enquiry.lookupCode || `ID ${enquiry.id}`;
  const items = menuItemLines(payload);

  return (
    <div
      className="account-sub-detail-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-bulk-detail-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="account-sub-detail-modal">
        <div className="account-sub-detail-modal__header">
          <div>
            <h2 id="account-bulk-detail-title">Bulk order details</h2>
            <p>
              {referenceId}
              {enquiry.lookupCode ? ` · ${enquiry.lookupCode}` : ''}
            </p>
          </div>
          <button type="button" className="account-sub-detail-modal__close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className="account-sub-detail-modal__body">
          <DetailGrid
            title="Reference"
            rows={[
              ['Reference ID', payload.referenceId || '—'],
              ['Lookup / tracking code', enquiry.lookupCode],
              ['Status', enquiry.status],
              ['Submitted', formatSubmittedAt(enquiry.createdAt)],
            ]}
          />

          <DetailGrid
            title="Your details"
            rows={[
              ['Name', enquiry.customerName || `${payload.firstName || ''} ${payload.lastName || ''}`.trim()],
              ['Phone', formatPhoneForDisplay(enquiry.customerPhone || payload.mobile) || enquiry.customerPhone || payload.mobile],
              ['Alternate phone', formatPhoneForDisplay(payload.altMobile) || payload.altMobile],
              ['Email', enquiry.customerEmail || payload.email],
              ['Organization', payload.organization],
            ]}
          />

          <DetailGrid
            title="Event details"
            rows={[
              ['Event type', eventType],
              ['Event date', formatEventDate(payload.eventDate)],
              ['Event time', formatEventTime(payload.eventTime)],
              ['Duration', payload.duration],
              ['Guests (pax)', payload.guests],
              ['Dietary', payload.dietary],
              ['Service type', payload.serviceType],
              ['Venue', payload.venue],
              ['City', payload.city],
              ['PIN code', payload.pincode],
            ]}
          />

          <DetailGrid
            title="Menu & notes"
            rows={[
              ['Menu', menuSummary(payload)],
              ['Budget / plate', payload.budgetPerPlate],
              ['Total budget', payload.totalBudget],
              ['Delivery instructions', payload.deliveryInstructions],
              ['Special requirements', payload.specialRequirements],
              ['How you heard', payload.howDidYouHear],
            ]}
          />

          {items.length ? (
            <section>
              <h3>Menu items</h3>
              <ul className="account-sub-detail-history">
                {items.map((item) => (
                  <li key={item}>{typeof item === 'string' ? item : item?.name || String(item)}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>

        <div className="account-sub-detail-modal__footer">
          <button type="button" className="account-sub-detail-modal__secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
